<script setup lang="ts">
import {
	ref,
	type PropType,
	onMounted,
	onUnmounted,
	computed,
	watch,
	nextTick,
} from 'vue'
import Swiper from 'swiper'
import { Autoplay, Navigation } from 'swiper/modules'
import { useWindowSize } from '@vueuse/core'
import 'swiper/css'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'
import { useBreakpoints } from '@/lib/utils'
import { checkProductSharingEnable } from '@/legacy/ts/productSharing'

export interface UserTestimonialsItem {
	type: string
	userType: string
	text: string
	name: string
}

export interface UserTestimonialsContent {
	title: string
	linkText: string
	items: UserTestimonialsItem[]
}

const props = defineProps({
	content: {
		type: Object as PropType<UserTestimonialsContent>,
		required: true,
	},
})

const { isLarge } = useBreakpoints()
const isProductSharingEnable = ref(checkProductSharingEnable())

const swiperEl = ref<HTMLElement | null>(null)
let swiper: Swiper | null = null

const { width } = useWindowSize()

// Computed values
const itemsCount = computed(() => props.content.items.length)

const canLoop = computed(() => itemsCount.value >= 2)

const showArrows = computed(() => itemsCount.value > 3)

const MOBILE_SPACE_BETWEEN = 16
const DESKTOP_SPACE_BETWEEN = 24
const DESKTOP_TRAILING_GAP = DESKTOP_SPACE_BETWEEN

const swiperOptions = computed(() => {
	const shouldLoop = canLoop.value
	const shouldCenterSlides = !isLarge.value
	const trailingGap = shouldCenterSlides ? 0 : DESKTOP_TRAILING_GAP

	return {
		modules: [Autoplay, Navigation],
		autoplay: {
			delay: 5000,
			disableOnInteraction: false,
		},
		loop: shouldLoop,
		slidesPerView: 'auto' as const,
		spaceBetween: MOBILE_SPACE_BETWEEN,
		speed: 500,
		centeredSlides: shouldCenterSlides,
		centeredSlidesBounds: shouldCenterSlides,
		slidesOffsetAfter: trailingGap,
		breakpoints: {
			1280: {
				spaceBetween: DESKTOP_SPACE_BETWEEN,
			},
		},
	}
})

const goPrev = () => {
	swiper?.slidePrev()
}

const goNext = () => {
	swiper?.slideNext()
}

const destroySwiper = () => {
	if (!swiper) return
	try {
		swiper.destroy(true, true)
	} catch (e) {
		console.error('Swiper destruction error:', e)
	}
	swiper = null
}

const initializeSwiper = async () => {
	if (!swiperEl.value || itemsCount.value === 0) return

	destroySwiper()

	await nextTick()
	await new Promise(resolve => {
		requestAnimationFrame(() => {
			requestAnimationFrame(resolve)
		})
	})

	if (!swiperEl.value) return

	try {
		swiper = new Swiper(swiperEl.value, swiperOptions.value)
	} catch (e) {
		console.error('Swiper initialization error:', e)
		swiper = null
	}
}

let resizeTimer: ReturnType<typeof setTimeout> | null = null
watch(width, async () => {
	if (resizeTimer) clearTimeout(resizeTimer)
	resizeTimer = setTimeout(async () => {
		await initializeSwiper()
	}, 150)
})

watch(itemsCount, async () => {
	await initializeSwiper()
})

onMounted(async () => {
	await initializeSwiper()
})

onUnmounted(() => {
	if (resizeTimer) clearTimeout(resizeTimer)
	destroySwiper()
})
</script>

<template>
	<div class="col-span-12">
		<div
			:class="[
				'flex flex-col justify-center gap-good-friends',
				isProductSharingEnable ? 'mt-[-32px]' : 'pt-60px mdp:pt-32px',
			]"
		>
			<h3 class="typography-h3-str px-20px mdp:px-0">{{ content.title }}</h3>
			<!-- content list with swiper -->
			<div class="relative">
				<div ref="swiperEl" class="swiper lg:!mr-[-6px]">
					<div class="swiper-wrapper !h-[188px] mdl:!h-[232px]">
						<div
							v-for="(item, index) in content.items"
							:key="index"
							class="swiper-slide !h-[188px] !w-[300px] mdl:!h-[232px] mdl:!w-[385px]"
						>
							<div
								class="flex h-full flex-col gap-16px rounded-xl bg-o-50 p-16px lg:p-24px"
							>
								<div class="flex gap-8px">
									<div class="flex flex-1 flex-col gap-4px">
										<div
											class="typography-s1-str line-clamp-2 max-w-[118px] text-ellipsis lg:max-w-[171px]"
										>
											{{ item.name }}
										</div>
										<div class="typography-c1-reg text-n-500">
											{{ item.userType }}
										</div>
									</div>
									<!-- label -->
									<div class="flex-shrink-0">
										<div
											class="relative z-10 mr-[-22px] max-w-[164px] rounded-l-full bg-o-500 px-8px py-4px text-right text-white lg:mr-[-30px] lg:max-w-[188px]"
											:class="[
												isLarge ? 'typography-s2-str' : 'typography-c2-reg',
											]"
										>
											{{ item.type }}
											<div
												class="absolute right-0 top-0 z-20 h-0 w-0 -translate-y-full border-b-[6px] border-r-[5px] border-b-[#BC5506] border-r-transparent"
											></div>
										</div>
									</div>
								</div>
								<div
									class="typography-s1-reg line-clamp-4 overflow-hidden text-ellipsis lg:line-clamp-5"
								>
									{{ item.text }}
								</div>
							</div>
						</div>
					</div>
				</div>
				<!-- Navigation buttons - only visible on desktop when items > 3 -->
				<BaseIcon
					v-if="showArrows"
					name="chevron_round"
					class="absolute left-0 top-1/2 z-10 hidden h-32px w-32px -translate-y-1/2 translate-x-[-50%] rotate-180 cursor-pointer mdl:block"
					@click="goPrev"
				/>
				<BaseIcon
					v-if="showArrows"
					name="chevron_round"
					class="absolute right-0 top-1/2 z-10 hidden h-32px w-32px -translate-y-1/2 translate-x-[50%] cursor-pointer mdl:block"
					@click="goNext"
				/>
			</div>
			<div
				class="flex flex-col items-center justify-center gap-8px lg:flex-row lg:justify-end lg:gap-16px"
			>
				<h5 class="typography-h5-str text-n-500">{{ content.linkText }}</h5>
				<a
					href="https://www.trustpilot.com/review/buyandship.today"
					target="_blank"
				>
					<BaseIcon
						name="trustpilot"
						class="h-[39px] w-[158px]"
						fill="fill-n-500"
					/>
				</a>
			</div>
		</div>
	</div>
</template>
